import {
  type FormEvent,
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import {
  SessionApiError,
  completeMfa,
  requestSignInCode,
  signInWithCode,
  signInWithPassword,
} from '../../session/client';
import { type SessionUser, useSession } from '../../session/SessionProvider';
import { messageFor } from '../LoginPage';
import { SoonLink } from '../SiteFooter';
import {
  type CourierForm,
  type CourierFormErrors,
  type CourierMe,
  type PharmacyOption,
  VEHICLE_CHOICES,
  courierDisplayName,
  needsLicence,
  pharmacyOptions,
  statusCopy,
  tidyPostcode,
  tidyRegistration,
  ukMobile,
  validRegistration,
  validateForm,
  vehicleLabel,
} from './courier-model';
import { applyAsCourier, getCourierMe, getDeliveryAreas, keepMobile, uploadEvidence } from './couriers-api';
import { EVIDENCE_ACCEPT, EvidenceError, prepareEvidence } from './evidence-file';

import './couriers.css';

/**
 * Become a courier — `Just Treated Become a Courier.dc.html`, built from the
 * frame's own markup where the platform backs it.
 *
 * The sign-up is real: the frame's details form, then the website's email code
 * (or a password, for an existing account) inline, then a photo of the
 * courier's ID — and the licence and registration for a motor vehicle — and
 * `driver/apply`, the same application the JT Delivery app sends. The
 * reference shown at the end is the platform's own courier record. Somebody
 * who has applied already sees where it stands, from `driver/me`.
 *
 * ## What the frame says that the platform cannot back, and so is not here
 *
 * - The DBS check, everywhere: "A basic DBS check", "we arrange and pay for it",
 *   "start your DBS check", "Your DBS check link comes from our screening
 *   partner", "Why do I need a DBS check?" (D12: no screening partner yet).
 * - "Weekly pay", "100% of your tips", "A UK bank account … for your weekly
 *   payments", "How do I get paid?" — no courier pay or payouts in the platform.
 * - "Most couriers are approved within a week" (D11: no invented numbers).
 * - "Short online training, about 45 minutes", the insulated bag and cool box,
 *   bookable busier slots, delivery zones — none exists.
 * - "iOS 15.5+ or Android 10+" — not the app's real requirement.
 * - "Self-employed" (hero and tip 04) — the courier's employment status is the
 *   owner's to state, so it is left for the owner to confirm.
 * - "insurance that covers delivery work" — not checked by the platform; left
 *   for the owner to confirm.
 * - "We've emailed … a link" — nothing is emailed; the decision is shown here
 *   and in the person's notifications, which is where the platform puts it.
 * - Bicycle: the platform offers no pedal bicycle to a new courier (D12), so it
 *   is drawn "Coming soon". Van, which the platform does offer, is added.
 */

const ARROW = 'M5 12h14M13 6l6 6-6 6';

function Svg({
  d,
  size = 22,
  stroke = '#5b2ede',
  width = 2,
}: {
  readonly d: string;
  readonly size?: number;
  readonly stroke?: string;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const BENEFITS = [
  {
    t: 'Flexible work',
    d: 'Take deliveries when it suits you. There are no shifts: you choose which deliveries to accept in the JT Delivery app.',
    icon: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11ZM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  },
  {
    t: 'Deliver near you',
    d: 'Apply to a pharmacy near your postcode, or to Just Treated to deliver for any pharmacy on Just Treated.',
    icon: 'M4 21V9l8-5 8 5v12M9 21v-6h6v6M4 21h16',
  },
  {
    t: 'Work that matters',
    d: "Get medicines to people who can't easily get out.",
    icon: 'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 7.5 2.8C19.5 15.4 12 20 12 20Z',
  },
] as const;

const NEEDS = [
  {
    t: 'Aged 18 or over',
    d: 'With the right to work in the UK.',
    icon: 'M7 3.5h10v17H7zM10 7.5h4M10 11h4M10 14.5h2',
  },
  {
    t: 'Photo ID',
    d: "A passport or driving licence. You'll add a photo of it when you apply.",
    icon: 'M3.5 6h17v12h-17zM8.5 12.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM5.8 15.5c.5-1.4 1.6-2 2.7-2s2.2.6 2.7 2M14 10h4M14 13h3',
  },
  {
    t: 'An e-bike, moped, car or van',
    d: 'Moped, car and van couriers also add their driving licence and registration number.',
    icon: 'M5.5 17a3 3 0 1 0 0-.01M18.5 17a3 3 0 1 0 0-.01M5.5 17 9 9h5l4.5 8M9 9 8 6.5H6',
  },
  { t: 'A smartphone', d: 'You deliver with the JT Delivery app.', icon: 'M8 3h8v18H8zM11 18h2' },
] as const;

const TIPS = [
  {
    n: '01',
    t: 'Have these ready',
    d: 'A photo of your ID (passport or driving licence). For a moped, car or van, your driving licence and registration number too.',
  },
  { n: '02', t: 'Match your ID', d: 'Enter your full legal name exactly as it appears on your ID.' },
  {
    n: '03',
    t: 'One account only',
    d: 'Each account can send one courier application. Apply from the account you will deliver with.',
  },
  { n: '04', t: 'Check your inbox', d: 'Your sign-in code comes by email. Check your spam folder too.' },
] as const;

const FAQ = [
  [
    'What are the requirements to deliver with Just Treated?',
    'You need to be 18 or over with the right to work in the UK, photo ID, a smartphone for the JT Delivery app, and an e-bike, moped, car or van. Moped, car and van couriers also add their driving licence and the vehicle’s registration number.',
  ],
  [
    'Can I deliver on a bicycle?',
    'Not yet — pedal bicycles are coming soon. You can apply now with an e-bike, moped, car or van.',
  ],
  [
    'Will I deliver prescription medicines?',
    'Yes, some orders contain them. You collect each order from the pharmacy and hand it over at the door, and the JT Delivery app checks a code at pickup and at delivery.',
  ],
  ['Can I use my own vehicle?', 'Yes. You deliver with your own e-bike, moped, car or van.'],
  [
    'Do I need to book my hours every week?',
    'No. There are no shifts to book. Once you’re approved, open the JT Delivery app and accept the deliveries you want from those waiting.',
  ],
  [
    'Can I deliver for a particular pharmacy?',
    'Yes. When you apply, choose a pharmacy near your postcode and that pharmacy reviews your application. Or apply to Just Treated and, once approved, deliver for any pharmacy on Just Treated.',
  ],
  [
    'How will I know if I’m approved?',
    'Come back to this page logged in, or check your Just Treated notifications. The decision appears in both.',
  ],
] as const;

type MeState =
  | { readonly kind: 'none' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly me: CourierMe }
  | { readonly kind: 'error' };

export function CouriersPage(): JSX.Element {
  const session = useSession();
  const user = session.status === 'signedIn' ? session.user : undefined;
  const [me, setMe] = useState<MeState>({ kind: 'none' });
  const [modal, setModal] = useState(false);
  const [faq, setFaq] = useState(0);
  const personId = user?.personId;

  const loadMe = useCallback(() => {
    setMe({ kind: 'loading' });
    getCourierMe()
      .then((value) => setMe({ kind: 'ready', me: value }))
      .catch(() => setMe({ kind: 'error' }));
  }, []);

  useEffect(() => {
    if (personId === undefined) {
      setMe({ kind: 'none' });
      return;
    }
    loadMe();
  }, [personId, loadMe]);

  const applied = me.kind === 'ready' && me.me.registered ? me.me : undefined;
  const ctaLabel = applied !== undefined ? 'See your application' : 'Sign up now';
  const open = (): void => setModal(true);

  return (
    <div className="jt-cr">
      <section className="jt-cr-hero">
        <div
          className="jt-cr-hero__img"
          role="img"
          aria-label="Smiling Just Treated courier in his car with a Just Treated delivery bag"
        />
        <span className="jt-cr-hero__fade" aria-hidden="true" />
        <span className="jt-cr-hero__shade" aria-hidden="true" />
        <div className="jt-cr-wrap jt-cr-hero__body">
          <span className="jt-cr-hero__pill">FLEXIBLE HOURS</span>
          <h1 className="jt-cr-hero__h1">Become a Just Treated courier</h1>
          <p className="jt-cr-hero__lede">Choose your own hours and deliver safely, on time.</p>
          <button type="button" className="jt-cr-btn jt-cr-btn--white" onClick={open}>
            {ctaLabel} <Svg d={ARROW} size={16} stroke="currentColor" width={2.6} />
          </button>
        </div>
      </section>

      {applied !== undefined ? <ApplicationCard me={applied} onOpen={open} /> : null}

      <section className="jt-cr-sec jt-cr-sec--first">
        <div className="jt-cr-wrap jt-cr-stack">
          <h2 className="jt-cr-h2">Your job benefits</h2>
          <div className="jt-cr-benefits">
            {BENEFITS.map((b) => (
              <article key={b.t} className="jt-cr-benefit">
                <span className="jt-cr-benefit__icon">
                  <Svg d={b.icon} />
                </span>
                <h3 className="jt-cr-benefit__t">{b.t}</h3>
                <p className="jt-cr-benefit__d">{b.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="needs" className="jt-cr-sec">
        <div className="jt-cr-wrap jt-cr-needs">
          <div className="jt-cr-needs__lead">
            <h2 className="jt-cr-h2">What you&apos;ll need</h2>
            <p className="jt-cr-needs__p">
              You&apos;re carrying people&apos;s medicines, so a few checks come first.
            </p>
            <div
              className="jt-cr-needs__img"
              role="img"
              aria-label="Just Treated courier on a moped outside a pharmacy"
            />
          </div>
          <div className="jt-cr-needs__grid">
            {NEEDS.map((n) => (
              <div key={n.t} className="jt-cr-need">
                <span className="jt-cr-need__icon">
                  <Svg d={n.icon} stroke="#5c3206" />
                </span>
                <span className="jt-cr-need__text">
                  <span className="jt-cr-need__t">{n.t}</span>
                  <span className="jt-cr-need__d">{n.d}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="jt-cr-sec">
        <div className="jt-cr-wrap jt-cr-stack">
          <h2 className="jt-cr-h2">Tips to speed up your registration</h2>
          <ol className="jt-cr-tips">
            {TIPS.map((t) => (
              <li key={t.n} className="jt-cr-tip">
                <span className="jt-cr-tip__n">
                  <span>{t.n}</span>
                  <span className="jt-cr-tip__line" />
                </span>
                <div className="jt-cr-tip__card">
                  <span className="jt-cr-tip__t">{t.t}</span>
                  <span className="jt-cr-tip__d">{t.d}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="jt-cr-sec jt-cr-sec--last">
        <div className="jt-cr-wrap jt-cr-stack jt-cr-stack--faq">
          <h2 className="jt-cr-h2">Frequently asked questions</h2>
          <div className="jt-cr-faq">
            {FAQ.map(([q, a], i) => {
              const isOpen = faq === i;
              return (
                <div key={q} className="jt-cr-faq__item">
                  <button
                    type="button"
                    className="jt-cr-faq__q"
                    aria-expanded={isOpen}
                    onClick={() => setFaq(isOpen ? -1 : i)}
                  >
                    {q}
                    <span className={`jt-cr-faq__plus${isOpen ? ' is-open' : ''}`}>
                      <Svg d="M12 5v14M5 12h14" size={18} width={2.6} />
                    </span>
                  </button>
                  <div className={`jt-cr-faq__a${isOpen ? ' is-open' : ''}`} hidden={!isOpen}>
                    <p>{a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="jt-cr-bar">
        <button type="button" className="jt-cr-btn" onClick={open}>
          {ctaLabel} <Svg d={ARROW} size={16} stroke="currentColor" width={2.6} />
        </button>
      </div>

      {modal ? (
        <SignupModal
          user={user}
          me={me.kind === 'ready' ? me.me : undefined}
          onApplied={(value) => setMe({ kind: 'ready', me: value })}
          onClose={() => setModal(false)}
        />
      ) : null}
    </div>
  );
}

function StatusBadge({
  tone,
  children,
}: {
  readonly tone: 'wait' | 'good' | 'bad';
  readonly children: ReactNode;
}): JSX.Element {
  return <span className={`jt-cr-badge jt-cr-badge--${tone}`}>{children}</span>;
}

/** The signed-in courier's own application, as the platform holds it. */
function ApplicationCard({
  me,
  onOpen,
}: {
  readonly me: Extract<CourierMe, { registered: true }>;
  readonly onOpen: () => void;
}): JSX.Element {
  const copy = statusCopy(me);
  return (
    <section className="jt-cr-sec jt-cr-sec--status" aria-labelledby="jt-cr-status-title">
      <div className="jt-cr-wrap">
        <div className="jt-cr-status">
          <div className="jt-cr-status__main">
            <StatusBadge tone={copy.tone}>{copy.badge}</StatusBadge>
            <h2 className="jt-cr-status__t" id="jt-cr-status-title">
              {copy.title}
            </h2>
            <p className="jt-cr-status__d">{copy.body}</p>
          </div>
          <dl className="jt-cr-status__facts">
            <div>
              <dt>Reference</dt>
              <dd>{me.id}</dd>
            </div>
            <div>
              <dt>Vehicle</dt>
              <dd>{vehicleLabel(me.vehicle)}</dd>
            </div>
          </dl>
          <button type="button" className="jt-cr-link" onClick={onOpen}>
            Details
          </button>
        </div>
      </div>
    </section>
  );
}

// -- The sign-up modal -----------------------------------------------------------

type Step =
  'details' | 'code' | 'password' | 'mfa' | 'checking' | 'documents' | 'sending' | 'done' | 'status';

const RESEND_SECONDS = 30;

function SignupModal({
  user,
  me,
  onApplied,
  onClose,
}: {
  readonly user: SessionUser | undefined;
  readonly me: CourierMe | undefined;
  readonly onApplied: (me: CourierMe) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const ids = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<Step>(me?.registered === true ? 'status' : 'details');
  const [current, setCurrent] = useState<CourierMe | undefined>(me);
  const [form, setForm] = useState<CourierForm>({
    first: '',
    last: '',
    email: user?.email ?? '',
    phone: '',
    postcode: '',
    vehicle: undefined,
    over18: false,
  });
  const [errors, setErrors] = useState<CourierFormErrors>({});
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Email code / password
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState('');
  const [password, setPassword] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [notice, setNotice] = useState('');
  const [mfaToken, setMfaToken] = useState<string | undefined>(undefined);
  const [cool, setCool] = useState(0);
  const [resent, setResent] = useState('');

  // Documents
  const [idFile, setIdFile] = useState<File | undefined>(undefined);
  const [licenceFile, setLicenceFile] = useState<File | undefined>(undefined);
  const [registration, setRegistration] = useState('');
  const [pharmacy, setPharmacy] = useState('');
  const [pharmacies, setPharmacies] = useState<readonly PharmacyOption[] | 'loading' | 'failed'>('loading');
  const [docErr, setDocErr] = useState<Partial<Record<'id' | 'licence' | 'reg' | 'send', string>>>({});
  const [mobileSaved, setMobileSaved] = useState(true);

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (cool <= 0) return undefined;
    const timer = window.setTimeout(() => setCool((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cool]);

  const set =
    (key: 'first' | 'last' | 'email' | 'phone' | 'postcode', errorKey: keyof CourierFormErrors) =>
    (value: string): void => {
      setForm((previous) => ({ ...previous, [key]: value }));
      setErrors((previous) => ({ ...previous, [errorKey]: undefined }));
      setFormErr('');
    };

  /** Signed in: does this person have an application already? */
  const afterSignIn = async (): Promise<void> => {
    setStep('checking');
    try {
      const value = await getCourierMe();
      setCurrent(value);
      if (value.registered) {
        onApplied(value);
        setStep('status');
        return;
      }
      setStep('documents');
    } catch (error) {
      setFormErr(error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.');
      setStep('details');
    }
  };

  // Load the pharmacies near the courier's postcode when the documents step opens.
  useEffect(() => {
    if (step !== 'documents') return undefined;
    let live = true;
    setPharmacies('loading');
    getDeliveryAreas(tidyPostcode(form.postcode))
      .then((areas) => {
        if (live) setPharmacies(pharmacyOptions(areas));
      })
      .catch(() => {
        if (live) setPharmacies('failed');
      });
    return () => {
      live = false;
    };
  }, [step, form.postcode]);

  const submitDetails = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const found = validateForm(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    if (user !== undefined) {
      await afterSignIn();
      return;
    }
    setBusy(true);
    try {
      await requestSignInCode(form.email.trim());
      setCode('');
      setCodeErr('');
      setCool(RESEND_SECONDS);
      setStep('code');
    } catch (error) {
      setErrors({ email: messageFor(error, 'request') });
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (code.length !== 6) return setCodeErr('Enter the 6-digit code from your email');
    setBusy(true);
    try {
      await signInWithCode(form.email.trim(), code);
      setBusy(false);
      await afterSignIn();
    } catch (error) {
      setBusy(false);
      if (error instanceof SessionApiError && error.code === 'AUTH_PASSWORD_SIGN_IN_REQUIRED') {
        setNotice(error.message);
        setStep('password');
        return;
      }
      setCodeErr(messageFor(error, 'code'));
    }
  };

  const resend = async (): Promise<void> => {
    if (cool > 0) return;
    setCool(RESEND_SECONDS);
    try {
      await requestSignInCode(form.email.trim());
      setResent(`New code sent to ${form.email.trim()}`);
    } catch (error) {
      setResent(messageFor(error, 'request'));
    }
  };

  const passwordSignIn = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (password === '') return setPasswordErr('Enter your password');
    setBusy(true);
    try {
      await signInWithPassword(form.email.trim(), password);
      setPassword('');
      setBusy(false);
      await afterSignIn();
    } catch (error) {
      setBusy(false);
      if (error instanceof SessionApiError && error.code === 'AUTH_MFA_REQUIRED') {
        const token = error.details.mfaToken;
        setMfaToken(typeof token === 'string' ? token : undefined);
        setPassword('');
        setCode('');
        setCodeErr('');
        setStep('mfa');
        return;
      }
      setPasswordErr(messageFor(error, 'password'));
    }
  };

  const verifyMfa = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (code.length !== 6) return setCodeErr('Enter the 6-digit code from your authenticator app');
    if (mfaToken === undefined) {
      setStep('password');
      return setPasswordErr('Log in with your password again.');
    }
    setBusy(true);
    try {
      await completeMfa(mfaToken, code);
      setBusy(false);
      await afterSignIn();
    } catch (error) {
      setBusy(false);
      setCodeErr(messageFor(error, 'mfa'));
    }
  };

  const vehicle = form.vehicle;
  const motor = vehicle !== undefined && needsLicence(vehicle);

  const send = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (vehicle === undefined || current === undefined) return;
    const found: typeof docErr = {};
    if (idFile === undefined) found.id = 'Add a photo of your ID';
    if (motor && licenceFile === undefined) found.licence = 'Add a photo of your driving licence';
    if (motor && !validRegistration(registration)) found.reg = "Enter the vehicle's registration number";
    if (Object.keys(found).length > 0) return setDocErr(found);
    if (idFile === undefined) return undefined;

    setStep('sending');
    setDocErr({});
    try {
      const idDoc = await prepareEvidence(idFile);
      const licenceDoc = motor && licenceFile !== undefined ? await prepareEvidence(licenceFile) : undefined;
      const idDocumentId = await uploadEvidence(current.personId, idDoc.filename, idDoc.bytes);
      const licenceDocumentId =
        licenceDoc !== undefined
          ? await uploadEvidence(current.personId, licenceDoc.filename, licenceDoc.bytes)
          : undefined;

      // The mobile number lives on the person; the application has no phone
      // field. Saving it is not allowed to stop the application.
      const mobile = ukMobile(form.phone);
      let saved = true;
      if (mobile !== undefined) {
        saved = await keepMobile(mobile).then(
          () => true,
          () => false,
        );
      }
      setMobileSaved(saved);

      await applyAsCourier({
        displayName: courierDisplayName(form.first, form.last),
        vehicle,
        idDocumentId,
        ...(licenceDocumentId !== undefined ? { licenceDocumentId } : {}),
        ...(motor ? { registrationNumber: tidyRegistration(registration) } : {}),
        ...(pharmacy !== '' ? { storefront: pharmacy } : {}),
      });
      const value = await getCourierMe();
      setCurrent(value);
      onApplied(value);
      setStep('done');
    } catch (error) {
      if (error instanceof SessionApiError && error.details.refusal === 'ALREADY_A_COURIER') {
        const value = await getCourierMe().catch(() => undefined);
        if (value !== undefined) {
          setCurrent(value);
          onApplied(value);
          setStep('status');
          return;
        }
      }
      setDocErr({
        send:
          error instanceof EvidenceError || error instanceof SessionApiError
            ? error.message
            : 'Something went wrong. Try again.',
      });
      setStep('documents');
    }
  };

  const title =
    step === 'done'
      ? "You're on your way"
      : step === 'status'
        ? 'Your courier application'
        : step === 'code'
          ? 'Check your inbox'
          : step === 'password'
            ? 'Log in with your password'
            : step === 'mfa'
              ? 'Enter your authenticator code'
              : step === 'documents' || step === 'sending'
                ? 'Add your documents'
                : 'Sign up to deliver';

  const registered = current?.registered === true ? current : undefined;

  return (
    <>
      <div className="jt-cr-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialog}
        className="jt-cr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
      >
        <div className="jt-cr-modal__head">
          <span className="jt-cr-modal__title" id={`${ids}-title`}>
            {title}
          </span>
          <button type="button" className="jt-cr-modal__close" aria-label="Close" onClick={onClose}>
            <Svg d="M6 6l12 12M18 6 6 18" size={15} stroke="#17112b" width={2.6} />
          </button>
        </div>

        {step === 'details' ? (
          <form className="jt-cr-form" noValidate onSubmit={(event) => void submitDetails(event)}>
            {user !== undefined ? (
              <p className="jt-cr-note" role="status">
                You&apos;re logged in as <b>{user.email}</b>. Your application is sent from this account.
              </p>
            ) : null}
            <div className="jt-cr-fields">
              <Field
                label="First name"
                value={form.first}
                onChange={set('first', 'first')}
                placeholder="Sam"
                autoComplete="given-name"
                error={errors.first}
              />
              <Field
                label="Last name"
                value={form.last}
                onChange={set('last', 'last')}
                placeholder="Taylor"
                autoComplete="family-name"
                error={errors.last}
              />
              <Field
                label="Email"
                value={form.email}
                onChange={set('email', 'email')}
                placeholder="name@email.com"
                type="email"
                autoComplete="email"
                error={errors.email}
                readOnly={user !== undefined}
              />
              <Field
                label="Mobile number"
                value={form.phone}
                onChange={set('phone', 'phone')}
                placeholder="07700 900123"
                type="tel"
                autoComplete="tel"
                error={errors.phone}
              />
              <Field
                label="Postcode"
                value={form.postcode}
                onChange={set('postcode', 'pc')}
                placeholder="M4 5BQ"
                autoComplete="postal-code"
                error={errors.pc}
              />
            </div>
            <div className="jt-cr-veh" role="group" aria-labelledby={`${ids}-veh`}>
              <span className="jt-cr-label" id={`${ids}-veh`}>
                How will you deliver?
              </span>
              <div className="jt-cr-veh__grid">
                {VEHICLE_CHOICES.map((choice) => {
                  const offered = choice.vehicle !== undefined;
                  const on = offered && form.vehicle === choice.vehicle;
                  return (
                    <button
                      key={choice.id}
                      type="button"
                      className={`jt-cr-veh__opt${on ? ' is-on' : ''}${offered ? '' : ' is-soon'}`}
                      aria-pressed={offered ? on : undefined}
                      aria-disabled={offered ? undefined : 'true'}
                      onClick={() => {
                        if (choice.vehicle === undefined) return;
                        setForm((previous) => ({ ...previous, vehicle: choice.vehicle }));
                        setErrors((previous) => ({ ...previous, veh: undefined }));
                      }}
                    >
                      <Svg d={choice.icon} />
                      {choice.label}
                      {offered ? null : <span className="jt-cr-veh__soon">Coming soon</span>}
                    </button>
                  );
                })}
              </div>
              {errors.veh !== undefined ? (
                <span className="jt-cr-err" role="alert">
                  {errors.veh}
                </span>
              ) : null}
            </div>
            <label className="jt-cr-check">
              <input
                type="checkbox"
                checked={form.over18}
                onChange={() => {
                  setForm((previous) => ({ ...previous, over18: !previous.over18 }));
                  setErrors((previous) => ({ ...previous, age: undefined }));
                }}
              />
              I&apos;m 18 or over and have the right to work in the UK
            </label>
            {errors.age !== undefined ? (
              <span className="jt-cr-err jt-cr-err--tight" role="alert">
                {errors.age}
              </span>
            ) : null}
            {formErr !== '' ? (
              <span className="jt-cr-err" role="alert">
                {formErr}
              </span>
            ) : null}
            <button type="submit" className="jt-cr-btn jt-cr-btn--full" disabled={busy} aria-busy={busy}>
              Continue <Svg d={ARROW} size={16} stroke="currentColor" width={2.6} />
            </button>
            <span className="jt-cr-fine">
              Next, you&apos;ll {user === undefined ? 'confirm your email and ' : ''}add a photo of your ID.
              We use your details to process your application. By continuing you agree to our{' '}
              <SoonLink label="Terms and Conditions" className="jt-cr-fine__link" /> and{' '}
              <SoonLink label="Privacy Notice" className="jt-cr-fine__link" />.
            </span>
          </form>
        ) : null}

        {step === 'code' || step === 'mfa' ? (
          <form
            className="jt-cr-form"
            noValidate
            onSubmit={(event) => void (step === 'code' ? verify(event) : verifyMfa(event))}
          >
            <p className="jt-cr-p">
              {step === 'code' ? (
                <>
                  We&apos;ve sent a 6-digit code to <b>{form.email.trim()}</b>. It expires in 10 minutes.
                </>
              ) : (
                'Open your authenticator app and enter the 6-digit code it shows for Just Treated.'
              )}
            </p>
            <label className="jt-cr-field">
              <span className="jt-cr-label" id={`${ids}-code`}>
                Verification code
              </span>
              <input
                aria-labelledby={`${ids}-code`}
                className={`jt-cr-input jt-cr-input--code${codeErr !== '' ? ' is-bad' : ''}`}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                  setCodeErr('');
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                aria-invalid={codeErr !== ''}
              />
              {codeErr !== '' ? (
                <span className="jt-cr-err" role="alert">
                  {codeErr}
                </span>
              ) : null}
            </label>
            <button type="submit" className="jt-cr-btn jt-cr-btn--full" disabled={busy} aria-busy={busy}>
              Continue <Svg d={ARROW} size={16} stroke="currentColor" width={2.6} />
            </button>
            {step === 'code' ? (
              <div className="jt-cr-row">
                <button
                  type="button"
                  className="jt-cr-link"
                  onClick={() => void resend()}
                  disabled={cool > 0}
                >
                  {cool > 0 ? `Resend in ${cool}s` : 'Resend code'}
                </button>
                <button
                  type="button"
                  className="jt-cr-link"
                  onClick={() => {
                    setNotice('');
                    setPasswordErr('');
                    setStep('password');
                  }}
                >
                  Use a password instead
                </button>
                <button type="button" className="jt-cr-link" onClick={() => setStep('details')}>
                  Change my details
                </button>
              </div>
            ) : null}
            {resent !== '' && step === 'code' ? (
              <span className="jt-cr-fine" role="status">
                {resent}
              </span>
            ) : null}
          </form>
        ) : null}

        {step === 'password' ? (
          <form className="jt-cr-form" noValidate onSubmit={(event) => void passwordSignIn(event)}>
            {notice !== '' ? (
              <p className="jt-cr-p" role="status">
                {notice}
              </p>
            ) : null}
            <p className="jt-cr-p">
              Log in as <b>{form.email.trim()}</b> to send your application from that account.
            </p>
            <label className="jt-cr-field">
              <span className="jt-cr-label" id={`${ids}-pw`}>
                Password
              </span>
              <input
                aria-labelledby={`${ids}-pw`}
                className={`jt-cr-input${passwordErr !== '' ? ' is-bad' : ''}`}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordErr('');
                }}
                aria-invalid={passwordErr !== ''}
              />
              {passwordErr !== '' ? (
                <span className="jt-cr-err" role="alert">
                  {passwordErr}
                </span>
              ) : null}
            </label>
            <button type="submit" className="jt-cr-btn jt-cr-btn--full" disabled={busy} aria-busy={busy}>
              Log in
            </button>
            <div className="jt-cr-row">
              <button type="button" className="jt-cr-link" onClick={() => setStep('details')}>
                Use an email code instead
              </button>
            </div>
          </form>
        ) : null}

        {step === 'checking' ? (
          <p className="jt-cr-p" role="status">
            Checking your account…
          </p>
        ) : null}

        {step === 'documents' || step === 'sending' ? (
          <form className="jt-cr-form" noValidate onSubmit={(event) => void send(event)}>
            <p className="jt-cr-p">
              {vehicle !== undefined ? `${vehicleLabel(vehicle)} courier. ` : ''}
              {motor
                ? 'Add a photo of your ID, your driving licence and the vehicle’s registration number.'
                : 'Add a photo of your ID.'}{' '}
              Customers see your name as <b>{courierDisplayName(form.first, form.last)}</b>.
            </p>
            <FileField
              label="Photo ID (passport or driving licence)"
              file={idFile}
              onFile={(file) => {
                setIdFile(file);
                setDocErr((e) => ({ ...e, id: undefined, send: undefined }));
              }}
              error={docErr.id}
            />
            {motor ? (
              <>
                <FileField
                  label="Driving licence"
                  file={licenceFile}
                  onFile={(file) => {
                    setLicenceFile(file);
                    setDocErr((e) => ({ ...e, licence: undefined, send: undefined }));
                  }}
                  error={docErr.licence}
                />
                <Field
                  label="Registration number"
                  value={registration}
                  onChange={(value) => {
                    setRegistration(value);
                    setDocErr((e) => ({ ...e, reg: undefined }));
                  }}
                  placeholder="AB12 CDE"
                  autoComplete="off"
                  error={docErr.reg}
                />
              </>
            ) : null}
            <label className="jt-cr-field">
              <span className="jt-cr-label" id={`${ids}-for`}>
                Who will you deliver for?
              </span>
              <select
                aria-labelledby={`${ids}-for`}
                className="jt-cr-input"
                value={pharmacy}
                onChange={(event) => setPharmacy(event.target.value)}
              >
                <option value="">Any pharmacy on Just Treated</option>
                {Array.isArray(pharmacies)
                  ? pharmacies.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))
                  : null}
              </select>
              <span className="jt-cr-fine">
                {pharmacies === 'loading'
                  ? `Finding pharmacies near ${tidyPostcode(form.postcode)}…`
                  : pharmacies === 'failed'
                    ? `We couldn't find pharmacies near ${tidyPostcode(form.postcode)}. You can still apply to Just Treated.`
                    : pharmacy === ''
                      ? 'Just Treated reviews your application.'
                      : 'That pharmacy reviews your application.'}
              </span>
            </label>
            {docErr.send !== undefined ? (
              <span className="jt-cr-err" role="alert">
                {docErr.send}
              </span>
            ) : null}
            <button
              type="submit"
              className="jt-cr-btn jt-cr-btn--full"
              disabled={step === 'sending'}
              aria-busy={step === 'sending'}
            >
              {step === 'sending' ? 'Sending…' : 'Send application'}
            </button>
          </form>
        ) : null}

        {step === 'done' && registered !== undefined ? (
          <div className="jt-cr-done">
            <span className="jt-cr-done__tick">
              <Svg d="m5 12.5 4.2 4.2L19 7" size={30} stroke="#156b49" width={2.6} />
            </span>
            <span className="jt-cr-done__p">
              Your application is in. Reference <b>{registered.id}</b>. {statusCopy(registered).body}
            </span>
            {!mobileSaved ? (
              <span className="jt-cr-fine">
                We couldn&apos;t save your mobile number. Add it in Your account.
              </span>
            ) : null}
            <button type="button" className="jt-cr-btn" onClick={onClose}>
              Done
            </button>
          </div>
        ) : null}

        {step === 'status' && registered !== undefined ? (
          <StatusPanel me={registered} onClose={onClose} />
        ) : null}
      </div>
    </>
  );
}

function StatusPanel({
  me,
  onClose,
}: {
  readonly me: Extract<CourierMe, { registered: true }>;
  readonly onClose: () => void;
}): JSX.Element {
  const copy = statusCopy(me);
  const submitted = me.verification?.submittedAt;
  return (
    <div className="jt-cr-statuspanel">
      <StatusBadge tone={copy.tone}>{copy.badge}</StatusBadge>
      <span className="jt-cr-statuspanel__t">{copy.title}</span>
      <p className="jt-cr-p">{copy.body}</p>
      <dl className="jt-cr-status__facts jt-cr-status__facts--panel">
        <div>
          <dt>Reference</dt>
          <dd>{me.id}</dd>
        </div>
        <div>
          <dt>Name customers see</dt>
          <dd>{me.displayName}</dd>
        </div>
        <div>
          <dt>Vehicle</dt>
          <dd>
            {vehicleLabel(me.vehicle)}
            {me.registrationNumber !== null ? ` · ${me.registrationNumber}` : ''}
          </dd>
        </div>
        {submitted !== undefined && submitted !== null ? (
          <div>
            <dt>Sent</dt>
            <dd>
              {new Date(submitted).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </dd>
          </div>
        ) : null}
      </dl>
      <button type="button" className="jt-cr-btn jt-cr-btn--full" onClick={onClose}>
        Done
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  error,
  readOnly = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly error?: string | undefined;
  readonly readOnly?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <label className="jt-cr-field">
      <span className="jt-cr-label" id={id}>
        {label}
      </span>
      <input
        aria-labelledby={id}
        className={`jt-cr-input${error !== undefined ? ' is-bad' : ''}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={error !== undefined}
        readOnly={readOnly}
      />
      {error !== undefined ? (
        <span className="jt-cr-err" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

function FileField({
  label,
  file,
  onFile,
  error,
}: {
  readonly label: string;
  readonly file: File | undefined;
  readonly onFile: (file: File | undefined) => void;
  readonly error?: string | undefined;
}): JSX.Element {
  return (
    <label className="jt-cr-field">
      <span className="jt-cr-label">{label}</span>
      <span
        className={`jt-cr-file${error !== undefined ? ' is-bad' : ''}${file !== undefined ? ' is-set' : ''}`}
      >
        <Svg d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" size={18} />
        <span className="jt-cr-file__name">{file !== undefined ? file.name : 'Choose a photo or PDF'}</span>
        <input
          type="file"
          accept={EVIDENCE_ACCEPT}
          aria-label={label}
          onChange={(event) => onFile(event.target.files?.[0] ?? undefined)}
        />
      </span>
      {error !== undefined ? (
        <span className="jt-cr-err" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}
